/**
 * A person's open claims behind one of the "Open worklist today" counts (review #158,
 * 2026-10-07: "if i click here it should take me to the corresponding claims").
 *
 * Not Claim detail. Claim detail is billed claims in months the person was paid; the
 * pile is whatever is open on their worklists at the snapshot, billed in any month or
 * not at all, so a person with 77 open claims can have none of them there. The rows are
 * the pile itself (src/data/openClaims.ts), and the build only ships a person's list
 * when it adds up to their counts — so the number clicked is the number listed.
 */
import { useMemo } from 'react'
import type { Person } from '@/data/selectors'
import { useOpenClaims, ROW_AGE } from '@/data/openClaims'
import { decodeClaims, type OpenClaim } from '@/data/terminatedClaims'
import { AGE_BUCKETS, type AgeBucketKey } from '@/measures/core'
import { DataTable } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { SidePanel } from '@/components/SidePanel'
import { openClaimColumns, OPEN_CLAIM_EXPORT } from '@/components/OpenClaimTable'
import { dateLong, int } from '@/format'

const MAX_ROWS = 500

export function OpenPilePanel({
  person,
  bucket,
  asOf: todayAsOf,
  monthEnd = null,
  onBucket,
  onClose,
}: {
  person: Person
  /** One recency bucket, or null for the whole pile. */
  bucket: AgeBucketKey | null
  asOf: string | null
  /**
   * The latest closed month end's pile instead of today's (review #401): its "Open at
   * month end" cell, with that cell's count. Its date and claims come from open.json.
   */
  monthEnd?: { count: number } | null
  onBucket: (b: AgeBucketKey | null) => void
  onClose: () => void
}) {
  const { data, error } = useOpenClaims()
  const pile = monthEnd ? data?.monthEnd?.people[person.employee] : data?.people[person.employee]
  const asOf = monthEnd ? (data?.monthEnd?.asOf ?? null) : todayAsOf
  const total = monthEnd ? monthEnd.count : person.openClaims
  const claims = useMemo<OpenClaim[]>(() => (pile ? decodeClaims(pile) : []), [pile])
  const shown = useMemo(
    () => (bucket ? claims.filter((c) => ROW_AGE[bucket].includes(c.age)) : claims),
    [claims, bucket],
  )
  const label = bucket ? AGE_BUCKETS.find((b) => b.key === bucket)!.label : 'All open claims'
  const lines: { key: AgeBucketKey | null; label: string; n: number }[] = [
    { key: null, label: 'All open claims', n: claims.length },
    ...AGE_BUCKETS.map((b) => ({
      key: b.key,
      label: b.label,
      n: claims.filter((c) => ROW_AGE[b.key].includes(c.age)).length,
    })),
  ]

  return (
    <SidePanel onClose={onClose} label={`Open claims of ${person.employee}: ${label}`} zIndex={60}>
      <span className="eyebrow" data-testid="open-pile-as-of">
        Open worklist{monthEnd ? ' at month end' : ''}
        {asOf ? ` as of ${dateLong(asOf)}` : ''}
      </span>
      <h2 className="page-title" style={{ marginTop: 4 }}>
        {person.employee}
      </h2>
      <p className="body-note" style={{ marginTop: 4 }}>
        {person.jobTitle} · {int(total)} open claims
      </p>

      {!data ? (
        <p className="body-note" style={{ marginTop: 14 }} data-testid="open-pile-status">
          {error ?? 'Loading the open claims…'}
        </p>
      ) : !pile ? (
        <p className="body-note" style={{ marginTop: 14 }} data-testid="open-pile-status">
          This person’s open claims are not in the current build: their list did not add up
          to the counts on their page, so it was left out rather than shown disagreeing.
        </p>
      ) : (
        <>
          <div className="tw-scopes" role="group" aria-label="Which claims">
            {lines
              .filter((l) => l.key === null || l.n > 0 || l.key === bucket)
              .map((l) => (
                <button
                  key={l.key ?? 'all'}
                  type="button"
                  className={l.key === bucket ? 'on' : undefined}
                  aria-pressed={l.key === bucket}
                  onClick={() => onBucket(l.key)}
                >
                  {l.label} · {int(l.n)}
                </button>
              ))}
          </div>
          <ExportBar
            filename="open-claims.csv"
            rows={shown}
            columns={[{ header: 'Employee', value: () => person.employee }, ...OPEN_CLAIM_EXPORT]}
          />
          <div className="card emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              storageKey="open-pile"
              sortParam="pile.sort"
              rows={shown}
              columns={openClaimColumns(true)}
              rowKey={(c) => c.claimId}
              initialSort="bill"
              initialDir="asc"
              maxRows={MAX_ROWS}
              maxHeight="calc(100vh - 280px)"
              empty="No open claims here."
            />
          </div>
          <p className="body-note" style={{ marginTop: 10 }}>
            {shown.length > MAX_ROWS &&
              `The table shows the first ${int(MAX_ROWS)} in the current sort; the export has all ${int(shown.length)}. `}
            The open pile at {monthEnd ? 'that month end' : 'the worklist snapshot'}, last
            worked by anyone, counted back from that date — not Claim
            detail’s billed claims, which most of these are not among. A claim with no
            visit behind it yet has no payer, bill date or balance.
          </p>
        </>
      )}
    </SidePanel>
  )
}
